import waEventEmitter from 'apps/analytics/services/waEventEmitter';
import routeMonitor from 'utils/routeMonitor';
import pageTypes from 'apps/analytics/utils/pageTypes';
import { getBrowserStats } from 'apps/analytics';
import CacheableSet from 'utils/CacheableSet';

const SENDING_INTERVAL = 1000;

// TODO: the class should be cut into some smaller ones (cutting by API purpose)
export default class PageViewTracker {
  pageComponentsSet = new CacheableSet(pageComponent => pageComponent.id);

  pageStatisticsToTrack = {};

  pageViewGTMData = {};

  requestParametersToTrack = {};

  constructor(stateSupplier, sendingIntervalTime = SENDING_INTERVAL) {
    this.stateSupplier = stateSupplier;
    this.sendingIntervalTime = sendingIntervalTime;
  }

  // API shared by React context

  setPageType = (pageType) => { this.pageType = pageType; };

  addPageComponent = (pageComponent) => {
    this.pageComponentsSet.enqueue(pageComponent);
    // we enqueue analytics data because every single component tracks itself so it would trigger multiple API requests,
    // to prevent multiple API calls we collect analytics details and track them in chunks every second
  };

  addPageStatistic = (field, value) => {
    this.pageStatisticsToTrack[field] = value;
  };

  addPageViewGTMData = data => {
    this.pageViewGTMData = {
      ...this.pageViewGTMData,
      ...data,
    };
  }

  addRequestParameter = (parameter, value) => {
    const parameterValues = this.requestParametersToTrack[parameter] || [];
    this.requestParametersToTrack[parameter] = parameterValues.concat(value);
  };

  // API used to manage the tracker

  startTrackingNewPageView = () => {
    const state = this.stateSupplier();

    if (!this.shouldTrackAnalytics(state)) {
      return;
    }

    this.lastTrackedPageView = state.analytics.pageView;

    const previousPath = routeMonitor.getOrigin() + routeMonitor.getPreviousRoute();
    const currentPath = routeMonitor.getOrigin() + routeMonitor.getCurrentRoute();
    const pageStatistics = {
      ...getBrowserStats(window),
      ...this.popPageStatistics(),
    };
    const pageComponents = this.popPageComponents();
    const requestParameters = this.popRequestParameters();
    const pageViewGTMData = this.popPageViewGTMData();
    this.sendPageView({
      pageType: this.pageType || pageTypes.NA,
      currentPath,
      previousPath,
      pageComponents,
      pageStatistics,
      pageViewGTMData,
      requestParameters,
    }, () => {
      this.pageViewCreated = true;
      this.startTrackingAnalyticsForNewPageView();
    });
  };

  shutdownPageViewTracking = () => {
    if (this.sendingInterval) {
      clearInterval(this.sendingInterval);
    }
    this.flushEnqueuedComponents();
    this.flushEnqueuedStatistics();
    this.flushEnqueuedRequestParameters();
    this.popPageViewGTMData();
    this.cleanupPageView();
  };

  // private methods

  popPageComponents = () => (this.pageComponentsSet.popAll());

  popPageStatistics = () => {
    const pageStatistics = { ...this.pageStatisticsToTrack };
    this.pageStatisticsToTrack = {};
    return pageStatistics;
  };

  popPageViewGTMData = () => {
    const pageViewGTMData = { ...this.pageViewGTMData };
    this.pageViewGTMData = {};
    return pageViewGTMData;
  };

  popRequestParameters = () => {
    const requestParameters = { ...this.requestParametersToTrack };
    this.requestParametersToTrack = {};
    return requestParameters;
  };

  sendPageView = (pageView, onPageViewCreated) => {
    waEventEmitter.trigger('wa:pageView', pageView, onPageViewCreated);
  };

  sendPageComponents = () => {
    const pageComponents = this.popPageComponents();
    if (pageComponents.length) {
      const pageView = this.lastTrackedPageView;
      waEventEmitter.trigger('wa:pageComponents', { pageView, pageComponents });
    }
  };

  // we send page components only if page view has been successfully created for current page
  // otherwise we just cleanup component related collections
  flushEnqueuedComponents = () => {
    if (this.pageViewCreated) {
      this.sendPageComponents();
    }
    this.pageComponentsSet.clear();
  };

  // only cleanup queue of page stats as all of them should have been sent with request to create new page view
  flushEnqueuedStatistics = () => (this.popPageStatistics());

  // only cleanup queue of request params as all of them should have been sent with request to create new page view
  flushEnqueuedRequestParameters = () => (this.popRequestParameters());

  startTrackingAnalyticsForNewPageView = () => {
    this.sendingInterval = setInterval(() => {
      this.sendPageComponents();
    }, this.sendingIntervalTime);
  };

  isNewPageView = (state) => this.lastTrackedPageView !== state.analytics.pageView;

  hasAnalyticsBetaFeature = (state) => state.user.betaFeatures.includes('ANALYTICS');

  isKnownPageType = () => !!this.pageType;

  shouldTrackAnalytics = (state) =>
    this.isNewPageView(state) && this.hasAnalyticsBetaFeature(state) && this.isKnownPageType();

  cleanupPageView = () => {
    this.pageType = null;
    this.pageViewCreated = false;
  };
}
